/**
 * Rate display utilities.
 *
 * Provides a single source of truth for:
 * 1. Determining whether to show rates in credits or USD
 * 2. Persisting rate display preference in a cookie for logged-out display
 *
 * Decision Matrix:
 * - Logged-IN users: Based directly on billing account type
 * - Logged-OUT users: Cookie (if set) > UTM source matching > default to USD
 */
import config from '@/config';

import { getCookie, setCookie } from './cookies';

export enum RateDisplayType {
  CREDITS = 'credits',
  USD = 'usd',
}

/**
 * Shown in place of a rate the API could not price — a null `credit_rate` /
 * `creditRate`, which happens when credit pricing is misconfigured or the viewer lacks
 * `view_credit_rate`. Never coerce a nullish rate to 0: "0 credits/hr" reads as "this
 * expert is free" rather than "we don't know the rate" (ENG-5283).
 */
export const RATE_UNAVAILABLE_LABEL = 'Rate unavailable';

/**
 * Convert an API credit rate (hundredths of a credit — 83 means 0.83 credits/hr) to
 * credits per hour, preserving "unknown" instead of collapsing it to 0.
 */
export function toCreditsPerHour(creditRate: number | null | undefined): number | null {
  if (creditRate === null || creditRate === undefined) return null;
  return creditRate / 100;
}

const RATE_DISPLAY_COOKIE = {
  // Persists the currency preference for LOGGED-OUT display only (see setFromBillingAccount).
  name: 'of_rate_display',
  maxAgeDays: 365, // 1 year per ticket
} as const;

interface BillingAccountForClassification {
  type?: string | null;
  isSMBAccount?: boolean | null;
  // ENG-5504: backend-authoritative display currency ("USD" | "OFC").
  display_currency?: string | null;
}

/**
 * Determine rate display type from a billing account.
 *
 * ENG-5504: the backend now emits `display_currency` (USD for SMB accounts, OFC
 * otherwise), so the display decision follows that field rather than being
 * re-derived from `isSMBAccount` on the client. The isSMBAccount/type inference
 * below is a fallback for callers that don't select `display_currency` (e.g. the
 * logged-out cookie seed).
 *
 * | display_currency | Result  |
 * |------------------|---------|
 * | OFC              | credits |
 * | USD              | usd     |
 * | (absent)         | fallback: isSMBAccount/postpaid -> usd, prepaid -> credits |
 */
function getRateDisplayFromBillingAccount(
  billingAccount: BillingAccountForClassification | null | undefined
): RateDisplayType {
  if (!billingAccount) return RateDisplayType.USD;
  if (billingAccount.display_currency === 'OFC') return RateDisplayType.CREDITS;
  if (billingAccount.display_currency === 'USD') return RateDisplayType.USD;
  // Fallback when display_currency is not selected/available.
  if (billingAccount.isSMBAccount === true) return RateDisplayType.USD;
  if (billingAccount.type === 'postpaid') return RateDisplayType.USD;
  if (billingAccount.type === 'prepaid') return RateDisplayType.CREDITS;
  return RateDisplayType.USD;
}

function hasRateDisplayCookie(): boolean {
  const value = getCookie(RATE_DISPLAY_COOKIE.name);
  return !!value && value.trim() !== '';
}

function getRateDisplayCookie(): RateDisplayType | null {
  const value = getCookie(RATE_DISPLAY_COOKIE.name);
  if (value === RateDisplayType.CREDITS) return RateDisplayType.CREDITS;
  if (value === RateDisplayType.USD) return RateDisplayType.USD;
  return null;
}

function setRateDisplayCookie(displayType: RateDisplayType): void {
  setCookie(RATE_DISPLAY_COOKIE.name, displayType, {
    maxAgeDays: RATE_DISPLAY_COOKIE.maxAgeDays,
  });
}

/**
 * Check if a UTM source indicates USD pricing should be shown.
 * Uses config.smbUtmSources from VITE_SMB_UTM_SOURCES env var.
 */
export function isUsdUtmSource(utmSource: string | null | undefined): boolean {
  if (!utmSource) return false;
  if (config.smbUtmSources.length === 0) return false;

  // Case-insensitive match, sanitize potential malformed values
  const sanitized = utmSource.split(/[?&]/)[0].toLowerCase().trim();
  if (!sanitized) return false;

  return config.smbUtmSources.some((source) => sanitized.includes(source));
}

/**
 * Extract utm_source from the current URL.
 */
export function getUtmSourceFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  return params.get('utm_source');
}

/**
 * Determine rate display type for a logged-in user.
 * Based directly on billing account type.
 */
export function getRateDisplayTypeForLoggedIn(
  billingAccount: BillingAccountForClassification | null | undefined
): RateDisplayType {
  return getRateDisplayFromBillingAccount(billingAccount);
}

/**
 * Determine rate display type for a logged-out user.
 *
 * Priority:
 * 1. Cookie "credits" → always credits
 * 2. Cookie "usd" → always USD
 * 3. No cookie + USD UTM → USD
 * 4. Default (no cookie, no USD UTM) → USD
 */
export function getRateDisplayTypeForLoggedOut(utmSource?: string | null): RateDisplayType {
  const cookie = getRateDisplayCookie();
  const utm = utmSource ?? getUtmSourceFromUrl();
  const hasUsdUtm = isUsdUtmSource(utm);

  if (cookie === RateDisplayType.CREDITS) return RateDisplayType.CREDITS;
  if (cookie === RateDisplayType.USD) return RateDisplayType.USD;
  if (hasUsdUtm) return RateDisplayType.USD;
  return RateDisplayType.USD;
}

/**
 * Unified rate display API.
 */
export const rateDisplay = {
  /**
   * Persist the rate-display currency derived from a billing account.
   *
   * Written on login, but consumed ONLY for LOGGED-OUT display
   * (`getRateDisplayTypeForLoggedOut`): it remembers the currency so a later
   * logged-out / anonymous visit on this browser shows the right rate. Logged-in
   * views never read this cookie — they derive currency live from the billing
   * account (`getRateDisplayTypeForLoggedIn`).
   */
  setFromBillingAccount(billingAccount: BillingAccountForClassification | null | undefined): void {
    const displayType = getRateDisplayFromBillingAccount(billingAccount);
    setRateDisplayCookie(displayType);
  },

  /**
   * Check if a rate display cookie exists (any non-empty value).
   * Use for presence checks (e.g., determining if user has visited before).
   */
  has(): boolean {
    return hasRateDisplayCookie();
  },
};
